import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Eyebrow,
  Heading,
  Input,
  Skeleton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  type StatusKey,
} from '@insolech/ui';
import { Search, ClipboardList } from 'lucide-react';
import { providerQueries, type ProviderStatusFilter } from '@insolech/sdk';
import { AppShell } from '@/components/layout/AppShell';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';

const STATUS_TO_KEY: Record<string, StatusKey> = {
  draft: 'draft',
  submitted: 'submitted',
  in_review: 'in_review',
  in_production: 'in_production',
  in_delivery: 'in_delivery',
  delivered: 'delivered',
  cancelled: 'cancelled',
  returned: 'returned',
  on_hold: 'on_hold',
  assigned_to_provider: 'assigned_to_provider',
  quality_check: 'quality_check',
};

const TABS: ReadonlyArray<{ key: ProviderStatusFilter; labelKey: string }> = [
  { key: 'pending_acceptance', labelKey: 'provider:tab_pending_acceptance' },
  { key: 'in_production', labelKey: 'provider:tab_in_production' },
  { key: 'quality_check', labelKey: 'provider:tab_quality_check' },
  { key: 'ready_to_ship', labelKey: 'provider:tab_ready_to_ship' },
  { key: 'shipped', labelKey: 'provider:tab_shipped' },
  { key: 'all', labelKey: 'provider:tab_all' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100] as const;

export function ProviderPipelinePage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['provider', 'common']);
  const { profileLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get('status') ?? 'pending_acceptance') as ProviderStatusFilter;
  const search = searchParams.get('q') ?? '';
  const page = Number(searchParams.get('page') ?? '0') || 0;
  const pageSizeFromUrl = Number(searchParams.get('size')) || 0;
  const pageSize: number = (PAGE_SIZE_OPTIONS as readonly number[]).includes(pageSizeFromUrl)
    ? pageSizeFromUrl
    : 25;

  const setParam = (k: string, v: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (v == null || v === '') next.delete(k);
    else next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  const params = { status, search, page, pageSize };
  const query = useQuery({
    ...providerQueries.providerPipelineQuery(supabase, params),
    enabled: !profileLoading,
    placeholderData: keepPreviousData,
  });

  const totalPages = Math.max(1, Math.ceil((query.data?.total ?? 0) / pageSize));

  // Debounced search
  const [draftSearch, setDraftSearch] = React.useState(search);
  React.useEffect(() => setDraftSearch(search), [search]);
  const setParamRef = React.useRef(setParam);
  setParamRef.current = setParam;
  const searchRef = React.useRef(search);
  searchRef.current = search;
  React.useEffect(() => {
    if (draftSearch === searchRef.current) return;
    const id = setTimeout(() => setParamRef.current('q', draftSearch || null), 300);
    return () => clearTimeout(id);
  }, [draftSearch]);

  return (
    <AppShell>
      <main className="px-6 py-5 max-w-[1400px] mx-auto">
        <Eyebrow>{t('provider:title_pipeline')}</Eyebrow>
        <Heading as="h1" size="h3" className="mt-2">
          {t('provider:title_pipeline')}
        </Heading>
        <p className="text-text-secondary mt-1 text-sm">
          {query.data
            ? `${query.data.total} line${query.data.total === 1 ? '' : 's'} matching the current filter`
            : t('provider:subtitle_pipeline')}
        </p>

        <Card className="mt-6">
          <CardHeader className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <Tabs
                value={status}
                onValueChange={(v) => {
                  setParam('status', v === 'pending_acceptance' ? null : v);
                  setParam('page', null);
                }}
              >
                <TabsList>
                  {TABS.map((tab) => (
                    <TabsTrigger
                      key={tab.key}
                      value={tab.key}
                      data-testid={`provider-tab-${tab.key}`}
                    >
                      {t(tab.labelKey)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-80">
                  <Search
                    aria-hidden="true"
                    className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted"
                  />
                  <Input
                    type="search"
                    placeholder={t('provider:search_placeholder')}
                    value={draftSearch}
                    onChange={(e) => setDraftSearch(e.target.value)}
                    className="ps-9"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-text-secondary whitespace-nowrap">
                  <span>{t('provider:rows_per_page')}</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setParam('size', e.target.value);
                      setParam('page', null);
                    }}
                    className="rounded-md border border-border-default bg-surface-elevated px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </CardHeader>
          <CardBody className="px-0 pb-0">
            <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-sand-50 shadow-[0_1px_0_0_var(--color-border-subtle)]">
                  <TableRow>
                    <TableHead>{t('provider:col_patient')}</TableHead>
                    <TableHead>{t('provider:col_patient_id')}</TableHead>
                    <TableHead>{t('provider:col_assigned')}</TableHead>
                    <TableHead>{t('provider:col_product')}</TableHead>
                    <TableHead>{t('provider:col_provider_notes')}</TableHead>
                    <TableHead>{t('provider:col_status')}</TableHead>
                    <TableHead>{t('provider:col_expected_delivery')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profileLoading || query.isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        <TableCell colSpan={7}>
                          <Skeleton className="h-6 w-full" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : query.isError ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <div className="text-error-700 text-sm py-4">
                          Could not load lines: {String(query.error)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (query.data?.rows.length ?? 0) === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <EmptyState
                          icon={<ClipboardList className="h-6 w-6" />}
                          title={t('provider:empty_pipeline_title')}
                          description={t('provider:empty_pipeline_body')}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    query.data!.rows.map((row) => (
                      <TableRow
                        key={row.order_line_id}
                        onClick={() => navigate(`/provider/orders/${row.order_line_id}`)}
                        className="cursor-pointer"
                        data-testid={`provider-pipeline-row-${row.order_line_id}`}
                      >
                        <TableCell className="font-medium text-text-primary">
                          {row.patient_first_name ?? <span className="text-text-muted">-</span>}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-text-secondary">
                          {row.patient_short_code ?? '-'}
                        </TableCell>
                        <TableCell className="text-text-secondary">
                          {row.assigned_at ? formatDate(row.assigned_at) : '-'}
                        </TableCell>
                        <TableCell>{formatProductLabel(row.product_name, row.line_type)}</TableCell>
                        <TableCell className="max-w-[260px] truncate text-text-secondary">
                          {row.provider_observations ?? (
                            <span className="text-text-muted">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge
                            status={(STATUS_TO_KEY[row.status] ?? 'draft') as StatusKey}
                            size="sm"
                          />
                        </TableCell>
                        <TableCell className="tabular text-text-secondary">
                          {row.expected_delivery_date
                            ? formatDate(row.expected_delivery_date)
                            : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardBody>
          {(query.data?.total ?? 0) > pageSize ? (
            <div className="flex items-center justify-between border-t border-border-subtle p-4 text-sm">
              <div className="text-text-muted">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-3 py-1 rounded-md border border-border-subtle disabled:opacity-50"
                  disabled={page === 0}
                  onClick={() => setParam('page', String(Math.max(0, page - 1)))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1 rounded-md border border-border-subtle disabled:opacity-50"
                  disabled={page + 1 >= totalPages}
                  onClick={() => setParam('page', String(page + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </Card>
      </main>
    </AppShell>
  );
}

function formatProductLabel(productName: string, lineType: string): string {
  if (productName && productName !== lineType) return productName;
  return formatLineType(lineType);
}

function formatLineType(s: string | undefined): string {
  if (!s) return '-';
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return s;
  }
}
